import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO_IMAGE } from "@/data/albums";
import { scrollToId } from "@/components/SmoothScroll";
import { ArrowDown } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.4 + i * 0.12 },
  }),
};

const Line = ({ children, i, className }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} initial="hidden" animate="show" custom={i} className={`block ${className || ""}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Ocean atmosphere" className="w-full h-full object-cover grayscale" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black" />
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 pb-24 md:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="font-mono text-[11px] uppercase tracking-[0.5em] text-[#8a8a8a] mb-6"
        >
          Artist · Producer · Developer · World-Builder · Executive
        </motion.p>

        <h1 className="font-serif font-light tracking-tight text-[#f2f0ec] text-[16vw] md:text-[13vw] leading-[0.82]">
          <Line i={0}>ØČËĀŃ</Line>
        </h1>

        <div className="mt-8 max-w-2xl">
          <div className="font-serif text-2xl md:text-4xl leading-[1.15] tracking-tight text-[#e5e2dc]">
            <Line i={2}>An ocean of sound,</Line>
            <Line i={3}>drowning in feeling.</Line>
          </div>
        </div>

        <motion.button
          data-testid="hero-scroll-cta"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          onClick={() => scrollToId("discography")}
          className="mt-12 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-[#8a8a8a] hover:text-[#f2f0ec] transition-colors group w-fit"
        >
          <span>Enter the archive</span>
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ArrowDown size={14} />
          </motion.span>
        </motion.button>
      </motion.div>
    </section>
  );
};
